/**
 * Horloge de capture : position de lecture du panneau vidéo de saisie, lisible
 * depuis le store sans dépendance React.
 *
 * Le timecode doit être figé **à la création de l'événement**, pas au flush : la
 * file de synchronisation part parfois plusieurs secondes après le geste (mode
 * hors-ligne, réseau de gymnase) et lire la position du lecteur au moment de la
 * sérialisation produirait une erreur croissante et invisible.
 *
 * Le panneau vidéo s'enregistre au montage et se désenregistre au démontage ;
 * hors saisie vidéo, aucune horloge n'est posée et le champ reste `undefined`
 * (la dérivation horloge murale de VS-15 prend alors le relais côté serveur).
 */
type CaptureClockReader = () => number | undefined;
type CaptureSeeker = (playbackMs: number) => void;

let read: CaptureClockReader | null = null;
let seek: CaptureSeeker | null = null;

/** Enregistre la source de position de lecture ; `null` la désenregistre. */
export function registerCaptureClock(fn: CaptureClockReader | null): void {
  read = fn;
}

/** Position de lecture courante en ms, ou `undefined` si aucune horloge n'est active. */
export function readCaptureTimecodeMs(): number | undefined {
  if (!read) return undefined;
  const value = read();
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value);
}

/**
 * Pilotage du même lecteur depuis les écrans de saisie (fil d'actions cliquable
 * du lot 3). Enregistré et retiré par le panneau, comme l'horloge.
 */
export function registerCaptureSeek(fn: CaptureSeeker | null): void {
  seek = fn;
}

/** Déplace le lecteur de saisie à une position de lecture, s'il est monté. */
export function seekCaptureVideoMs(playbackMs: number): void {
  seek?.(playbackMs);
}

/** Un panneau vidéo de saisie est monté et pilotable. */
export function hasCaptureVideo(): boolean {
  return seek !== null;
}
